import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { fmtMoney } from 'utilities/format';

const emptyInvoice = () => ({
  currency: 'USD',
  customer: { name: '', email: '', country: '', region: '', city: '', postal_code: '', tax_id: '' },
  line_items: [{ description: '', quantity: 1, unit_price: 0, tax_code: '*' }],
  discount_total: 0,
  shipping_total: 0,
  notes: '',
});

/**
 * Invoice create/edit dialog. Two save paths: "Save draft" keeps it
 * editable; "Save & issue" finalizes a number and locks line items.
 */
export default function InvoiceDialog({ open, onClose, brandId, editing, onSaved }) {
  const [form, setForm] = useState(emptyInvoice());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? {
      ...emptyInvoice(),
      ...editing,
      customer: { ...emptyInvoice().customer, ...(editing.customer || {}) },
      line_items: editing.line_items?.length ? editing.line_items : emptyInvoice().line_items,
    } : emptyInvoice());
  }, [open, editing]);

  const setItem = (i, patch) =>
    setForm((f) => ({ ...f, line_items: f.line_items.map((it, j) => j === i ? { ...it, ...patch } : it) }));
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, tax_code: '*' }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }));

  const save = async (issue = false) => {
    setSaving(true);
    try {
      const call = editing
        ? API.updateInvoice({ brand_id: brandId, _id: editing._id, patch: form })
        : API.createInvoice({ brand_id: brandId, patch: form });
      const saved = await call;
      const id = saved?._id || editing?._id;
      if (issue && id) {
        await API.issueInvoice({ brand_id: brandId, _id: id });
      }
      toast.success(issue ? 'Issued' : 'Saved');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? `Invoice ${editing.number || '(draft)'}` : 'New invoice'}
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <Typography variant='body2' fontWeight='700'>Customer</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px'>
            <TextField
              size='small'
              label='Name'
              value={form.customer.name}
              onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='Email'
              value={form.customer.email}
              onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })}
              sx={{ flex: 2 }}
            />
            <TextField
              size='small'
              label='Country'
              value={form.customer.country}
              onChange={(e) => setForm({ ...form, customer: { ...form.customer, country: e.target.value.toUpperCase().slice(0, 2) } })}
              sx={{ maxWidth: 110 }}
            />
            <TextField
              size='small'
              label='Region'
              value={form.customer.region}
              onChange={(e) => setForm({ ...form, customer: { ...form.customer, region: e.target.value.toUpperCase().slice(0, 6) } })}
              sx={{ maxWidth: 110 }}
            />
            <TextField
              size='small'
              label='Tax ID'
              value={form.customer.tax_id}
              onChange={(e) => setForm({ ...form, customer: { ...form.customer, tax_id: e.target.value } })}
              sx={{ maxWidth: 160 }}
            />
          </Stack>

          <Divider />

          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='body2' fontWeight='700'>Line items</Typography>
            <Button size='small' startIcon={<AddIcon />} onClick={addItem}>Add item</Button>
          </Stack>

          {form.line_items.map((it, i) => (
            <Box
              key={i}
              sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px' alignItems='center'>
                <TextField
                  size='small'
                  label='Description'
                  value={it.description || ''}
                  onChange={(e) => setItem(i, { description: e.target.value })}
                  sx={{ flex: 3 }}
                />
                <TextField
                  size='small'
                  type='number'
                  label='Qty'
                  value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: Number(e.target.value) })}
                  sx={{ maxWidth: 80 }}
                />
                <TextField
                  size='small'
                  type='number'
                  label='Unit price'
                  inputProps={{ step: '0.01' }}
                  value={it.unit_price}
                  onChange={(e) => setItem(i, { unit_price: Number(e.target.value) })}
                  sx={{ maxWidth: 120 }}
                />
                <TextField
                  size='small'
                  label='Tax code'
                  value={it.tax_code || '*'}
                  onChange={(e) => setItem(i, { tax_code: e.target.value || '*' })}
                  sx={{ maxWidth: 100 }}
                />
                <GlassIconButton size='small' color='error' label='Remove line item' onClick={() => rmItem(i)}>
                  <DeleteIcon fontSize='small' />
                </GlassIconButton>
              </Stack>
            </Box>
          ))}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px'>
            <TextField
              size='small'
              label='Currency'
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })}
              sx={{ maxWidth: 130 }}
            />
            <TextField
              size='small'
              type='number'
              label='Discount'
              inputProps={{ step: '0.01' }}
              value={form.discount_total}
              onChange={(e) => setForm({ ...form, discount_total: Number(e.target.value) })}
              sx={{ maxWidth: 160 }}
            />
            <TextField
              size='small'
              type='number'
              label='Shipping'
              inputProps={{ step: '0.01' }}
              value={form.shipping_total}
              onChange={(e) => setForm({ ...form, shipping_total: Number(e.target.value) })}
              sx={{ maxWidth: 160 }}
            />
          </Stack>

          <TextField
            size='small'
            label='Notes'
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />

          {editing && editing.tax_summary?.length > 0 && (
            <Box p='12px' bgcolor='action.hover' borderRadius='10px'>
              <Typography variant='body2' fontWeight='700' mb='4px'>Computed totals</Typography>
              <Typography variant='body2'>Subtotal: {fmtMoney(editing.subtotal, editing.currency)}</Typography>
              {editing.tax_summary.map((t, i) => (
                <Typography variant='body2' key={i}>
                  {t.name} ({(t.rate_basis_points / 100).toFixed(2)}%): {fmtMoney(t.amount, editing.currency)}
                </Typography>
              ))}
              <Typography variant='body2' fontWeight='700' mt='4px'>
                Grand total: {fmtMoney(editing.grand_total, editing.currency)}
              </Typography>
              <Typography variant='caption' color='text.secondary'>Totals are re-calculated on save.</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => save(false)} disabled={saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <Button variant='contained' onClick={() => save(true)} disabled={saving}>
          {saving ? 'Working…' : 'Save & issue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
